import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Label,
  QueryLoaderWrapper,
  Text
} from '@companion-professional/components';
import { CheckupTag, FuzzyPatientClientSearchResult } from '@companion-professional/dctypes';
import { useFuzzyPatientClientSearch } from '../../hooks/useFuzzyPatientClientSearch';
import { useCreatePetAndCheckup } from '../../hooks/useSavePetName';
import { useCheckupUrl } from '../../hooks/useCheckupUrl';
import { useAuthStore } from '../../state/auth';
import { USER_SELECTABLE_LOCATION_TAGS } from '../../lib/config';
import { CheckupLocationSelectionButtons } from '../CheckupDialogComponents';
import { LoadingMessage } from '../LoadingMessage';
import { CheckupData } from './CreateQuickCheckup';

interface FuzzyPetSearchFormProps {
  setCheckupData: (checkupData: CheckupData) => void;
}

const formatOwnerLine = (r: FuzzyPatientClientSearchResult): string => {
  const name = [r.owner_first_name, r.owner_last_name].filter(Boolean).join(' ');
  if (name && r.owner_email) {
    return `${name} (${r.owner_email})`;
  }
  return name || r.owner_email;
};

// FuzzyPetSearchForm is the variant of the Quick Checkup creation flow used when the backend supports fuzzy patient/
// client search. The user types in a search box, results are fetched from
// /api/v3/int/fuzzy_patient_client_search, and once a pet is picked the user selects a location tag to create the
// checkup.
export const FuzzyPetSearchForm: FunctionComponent<FuzzyPetSearchFormProps> = ({ setCheckupData }) => {
  const [search, setSearch] = useState('');
  const [selectedPet, setSelectedPet] = useState<FuzzyPatientClientSearchResult | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const locationButtonsRef = useRef<HTMLDivElement>(null);

  // Manage focus as the user moves between the two visual states. Without this, Radix's dialog autofocus lands on
  // the close (X) button — the form isn't mounted yet when the dialog opens because the supports-fuzzy-search check
  // is still pending — and later, clearing the selected pet or selecting a CommandItem unmounts the focused
  // element, dropping focus to the document body.
  useEffect(() => {
    if (selectedPet) {
      locationButtonsRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    } else {
      searchInputRef.current?.focus();
    }
  }, [selectedPet]);

  const { results, isPending: searchIsPending, error: searchError, hasEnoughInput, minSearchLength } =
    useFuzzyPatientClientSearch(search);
  const { createPetAndCheckup, isPending: createIsPending, error: createError } = useCreatePetAndCheckup();
  const { getCheckupUrl, isPending: getCheckupUrlIsPending, error: getCheckupUrlError } = useCheckupUrl();

  const handleSelectLocation = async (tag: CheckupTag) => {
    if (!selectedPet) {
      return;
    }
    const clinicId = useAuthStore.getState().parsedToken?.clinic_id;
    if (!clinicId) {
      throw new Error('No clinic defined in the user token');
    }
    const res = await createPetAndCheckup({
      clinicId,
      clinicPetId: selectedPet.patient_id,
      locationTag: tag,
      petName: selectedPet.patient_name,
      species: selectedPet.species
    });
    const checkupUrl = getCheckupUrl(res.id);
    if (checkupUrl) {
      setCheckupData({
        clinicPetId: selectedPet.patient_id,
        checkupId: res.id,
        checkupUrl
      });
    }
  };

  return (
    <div>
      <DialogHeader>
        <DialogTitle>Create Quick Checkup</DialogTitle>
        <DialogDescription>
          Search by pet name, client name, or email to find an existing pet, then choose where the checkup will be
          taken.
        </DialogDescription>
      </DialogHeader>
      <QueryLoaderWrapper
        isPending={getCheckupUrlIsPending || createIsPending}
        error={getCheckupUrlError || createError}
        showChildrenOnError
      >
        <div className="mt-4 flex flex-col space-y-8">
          {selectedPet ? (
            <div className="flex flex-col space-y-2">
              <Label>Selected Pet</Label>
              <div className="border-input flex flex-row items-center justify-between rounded-md border p-3">
                <div className="flex flex-col">
                  <Text as="span" weight="strong">
                    {selectedPet.patient_name}
                  </Text>
                  <Text as="span" size="sm" tone="muted">
                    {formatOwnerLine(selectedPet)}
                  </Text>
                </div>
                <Button
                  variant="ghost"
                  size="small"
                  type="button"
                  aria-label="Clear selected pet"
                  onClick={() => {
                    setSelectedPet(null);
                    setSearch('');
                  }}
                >
                  <X className="size-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col space-y-2">
              <Label>Find a Pet</Label>
              <Command
                shouldFilter={false}
                className="border-input rounded-md border"
              >
                <CommandInput
                  ref={searchInputRef}
                  placeholder="Pet name, client name, or email..."
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList>
                  {!hasEnoughInput ? (
                    <Text size="sm" tone="muted" className="py-6 text-center">
                      Type at least {minSearchLength} characters to search.
                    </Text>
                  ) : searchIsPending ? (
                    <LoadingMessage size="sm" className="justify-center py-6">
                      Searching...
                    </LoadingMessage>
                  ) : searchError ? (
                    <Text size="sm" tone="destructive" className="py-6 text-center">
                      Error searching for pets.
                    </Text>
                  ) : results.length === 0 ? (
                    <CommandEmpty>No matching pets found.</CommandEmpty>
                  ) : (
                    results.map((r) => (
                      <CommandItem
                        key={r.patient_id}
                        value={r.patient_id}
                        onSelect={() => setSelectedPet(r)}
                      >
                        <div className="flex flex-col">
                          <Text as="span" weight="strong">
                            {r.patient_name}
                          </Text>
                          <Text as="span" size="xs" tone="muted">
                            {formatOwnerLine(r)}
                          </Text>
                        </div>
                      </CommandItem>
                    ))
                  )}
                </CommandList>
              </Command>
            </div>
          )}
          {selectedPet ? (
            <div ref={locationButtonsRef} className="flex flex-col space-y-2">
              <Label description="Select the location that the video will be taken.">Create Checkup For:</Label>
              {/* We expect every PMS to require a species on all animals, so a selected pet should always have one.
                  If that turns out not to be true for some PMS, we'll need to add logic here to prompt the user for
                  the species rather than blocking checkup creation. */}
              {!selectedPet.species ? (
                <Text size="sm" tone="destructive">
                  This pet does not have a species set in the PMS. A species is required to create a checkup. Please
                  set the species in the PMS and try again.
                </Text>
              ) : createIsPending || getCheckupUrlIsPending ? (
                <LoadingMessage className="ml-3">Creating Checkup...</LoadingMessage>
              ) : (
                <CheckupLocationSelectionButtons
                  onTagSelect={handleSelectLocation}
                  locationTags={USER_SELECTABLE_LOCATION_TAGS}
                />
              )}
            </div>
          ) : null}
        </div>
      </QueryLoaderWrapper>
    </div>
  );
};
